// Interactive editor viewport (three.js): orbit navigation, selection,
// transform gizmo, the scene camera's frustum, and timeline posing.

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { buildObjects, pose, readTransform, threeCamera } from './render';
import type { SceneDesc, Transform } from './types';

export type GizmoMode = 'translate' | 'rotate' | 'scale';

export interface ViewportEvents {
  select(id: string | null): void;
  /** Gizmo drag finished on node `id` with its new local transform. */
  transform(id: string, t: Transform): void;
}

export class Viewport {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private editorCam: THREE.PerspectiveCamera;
  private orbit: OrbitControls;
  private gizmo: TransformControls;
  private content: THREE.Group | null = null;
  private objects = new Map<string, THREE.Object3D>();
  private material = new THREE.MeshNormalMaterial({ side: THREE.DoubleSide });
  private camHelper: THREE.CameraHelper | null = null;
  private selection: THREE.BoxHelper | null = null;
  private selected: string | null = null;
  private desc: SceneDesc | null = null;
  private time = 0;
  private raf = 0;
  private resizeObs: ResizeObserver;
  private dragging = false;
  /** Look through the scene camera instead of the editor camera. */
  viewThroughCamera = false;

  constructor(
    private canvas: HTMLCanvasElement,
    private events: ViewportEvents,
  ) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    this.scene.background = new THREE.Color(0x14161c);
    this.editorCam = new THREE.PerspectiveCamera(45, 1, 0.05, 500);
    this.editorCam.position.set(6, 4, 12);
    this.orbit = new OrbitControls(this.editorCam, canvas);
    this.orbit.enableDamping = true;
    this.gizmo = new TransformControls(this.editorCam, canvas);
    this.gizmo.addEventListener('dragging-changed', (e) => {
      this.dragging = !!e.value;
      this.orbit.enabled = !e.value;
      if (!e.value && this.selected) {
        const obj = this.objects.get(this.selected);
        if (obj) this.events.transform(this.selected, readTransform(obj));
      }
    });
    this.gizmo.addEventListener('objectChange', () => this.selection?.update());
    this.scene.add(this.gizmo.getHelper());
    const grid = new THREE.GridHelper(20, 20, 0x3a4050, 0x232733);
    grid.rotation.x = Math.PI / 2;
    this.scene.add(grid);
    this.scene.add(new THREE.AxesHelper(1.5));

    let down: { x: number; y: number } | null = null;
    canvas.addEventListener('pointerdown', (e) => (down = { x: e.clientX, y: e.clientY }));
    canvas.addEventListener('pointerup', (e) => {
      if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) < 4 && !this.dragging) this.pick(e);
      down = null;
    });
    this.resizeObs = new ResizeObserver(() => this.resize());
    this.resizeObs.observe(canvas);
    this.resize();
    const loop = () => {
      this.raf = requestAnimationFrame(loop);
      this.orbit.update();
      this.render();
    };
    loop();
  }

  dispose() {
    cancelAnimationFrame(this.raf);
    this.resizeObs.disconnect();
    this.gizmo.dispose();
    this.orbit.dispose();
    this.clearContent();
    this.renderer.dispose();
  }

  private clearContent() {
    if (!this.content) return;
    this.gizmo.detach();
    this.content.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
    this.scene.remove(this.content);
    this.content = null;
  }

  /** Rebuilds the scene graph from `desc` (called after every edit). */
  setScene(desc: SceneDesc) {
    if (this.dragging) return;
    this.desc = desc;
    this.clearContent();
    const built = buildObjects(desc, this.material);
    this.content = built.root;
    this.objects = built.objects;
    this.scene.add(this.content);
    this.setTime(this.time);
    this.select(this.selected && this.objects.has(this.selected) ? this.selected : null);
  }

  setTime(t: number) {
    this.time = t;
    if (!this.desc || this.dragging) return;
    const cam = pose(this.desc, this.objects, t);
    this.updateCameraHelper(cam);
    this.selection?.update();
  }

  private sceneCam: THREE.Camera | null = null;

  private updateCameraHelper(c: SceneDesc['camera']) {
    if (this.camHelper) {
      this.scene.remove(this.camHelper);
      this.camHelper.dispose();
    }
    const aspect = this.canvas.clientWidth / Math.max(1, this.canvas.clientHeight);
    this.sceneCam = threeCamera(c, aspect);
    this.camHelper = new THREE.CameraHelper(this.sceneCam);
    this.camHelper.visible = !this.viewThroughCamera;
    this.scene.add(this.camHelper);
  }

  setGizmoMode(m: GizmoMode) {
    this.gizmo.setMode(m);
  }

  select(id: string | null) {
    this.selected = id;
    if (this.selection) {
      this.scene.remove(this.selection);
      this.selection.dispose();
      this.selection = null;
    }
    const obj = id ? this.objects.get(id) : null;
    if (obj) {
      this.gizmo.attach(obj);
      this.selection = new THREE.BoxHelper(obj, 0xe8c26a);
      this.scene.add(this.selection);
    } else {
      this.gizmo.detach();
    }
  }

  /** Frames the content in the editor camera. */
  focus() {
    if (!this.content) return;
    const box = new THREE.Box3().setFromObject(this.content);
    if (box.isEmpty()) return;
    const c = box.getCenter(new THREE.Vector3());
    const r = box.getSize(new THREE.Vector3()).length() / 2;
    this.orbit.target.copy(c);
    this.editorCam.position.copy(c).add(new THREE.Vector3(0.4, 0.3, 1).normalize().multiplyScalar(r * 3 + 2));
  }

  private pick(e: PointerEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.activeCamera());
    const meshes: THREE.Object3D[] = [];
    this.content?.traverse((o) => (o as THREE.Mesh).isMesh && o.visible && meshes.push(o));
    const hit = ray.intersectObjects(meshes, false)[0];
    const id = (hit?.object.userData.id as string | undefined) ?? null;
    this.events.select(id);
  }

  private activeCamera(): THREE.Camera {
    return this.viewThroughCamera && this.sceneCam ? this.sceneCam : this.editorCam;
  }

  private resize() {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.editorCam.aspect = w / h;
    this.editorCam.updateProjectionMatrix();
    if (this.desc) this.setTime(this.time);
  }

  private render() {
    if (this.camHelper) this.camHelper.visible = !this.viewThroughCamera;
    this.gizmo.getHelper().visible = !this.viewThroughCamera;
    this.renderer.render(this.scene, this.activeCamera());
  }
}
